import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { LogOut, Building2, Bell, Clock, Trash2, Gift, RefreshCw, Package, Menu, X, LayoutDashboard, Users, Activity, History, FileText, Settings } from 'lucide-react';
import axios from 'axios';
import { NODE_API_URL } from '../config';
import logo from '../unify_logo.png';

const Layout = ({ onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user')) || {};
  const displayName = user.name || user.username || 'Admin';
  const initial = displayName.charAt(0).toUpperCase();

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const [notifications, setNotifications] = useState([]);
  const [showNotif, setShowNotif] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const notifRef = useRef(null);
  const prevNotifsRef = useRef([]);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${NODE_API_URL}/notifications/unseen?_t=${Date.now()}`);
      if (Array.isArray(res.data)) {
        setNotifications(res.data);
        
        // Trigger Native Desktop Notifications for new items
        if ('Notification' in window && Notification.permission === 'granted') {
          const currentIds = prevNotifsRef.current.map(n => n.rx_id);
          const newItems = res.data.filter(n => !currentIds.includes(n.rx_id));
          
          newItems.forEach(n => {
            const title = n.action_type || 'Unify CRM Notification';
            const body = n.details || `${n.full_name || 'Patient'} - ${n.drug_name || 'System Alert'}`;
            new Notification(title, { body, icon: logo });
          });
        }
        prevNotifsRef.current = res.data;
      }
    } catch (error) { console.error("Error fetching notifications", error); }
  };

  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      await Notification.requestPermission();
      // Trigger a tiny state update to re-render the button away if granted
      setNotifications(prev => [...prev]);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (notifRef.current && !notifRef.current.contains(event.target)) setShowNotif(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [notifRef]);

  const handleNotificationClick = async (rxId, patientId, type) => {
    try {
      if (type !== 'Birthday') await axios.post(`${NODE_API_URL}/notifications/seen`, { rx_id: rxId, type });
      setNotifications(prev => prev.filter(n => n.rx_id !== rxId));
      setShowNotif(false);
      
      if (type === 'Activity' || !patientId) {
        navigate(`/history`);
      } else {
        navigate(`/patient/${patientId}`);
      }
    } catch (error) { 
      if (type === 'Activity' || !patientId) navigate(`/history`);
      else navigate(`/patient/${patientId}`);
    }
  };

  const navItems = [
    { name: 'Dashboard', path: '/', icon: LayoutDashboard },
    { name: 'Patients', path: '/patients', icon: Users },
    { name: 'Tracker', path: '/tracker', icon: Activity },
    { name: 'History', path: '/history', icon: History },
    { name: 'System Logs', path: '/logs', icon: FileText },
  ];

  return (
    <div className="flex h-screen overflow-hidden font-sans text-slate-800" style={{ backgroundColor: '#A5D6A7' }}>
      
      {/* MOBILE SIDEBAR OVERLAY */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* RESPONSIVE SIDEBAR */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-teal-800 text-white border-r border-teal-700 shadow-xl transform transition-transform duration-300 ease-in-out flex flex-col
        lg:translate-x-0 lg:static
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Sidebar Header */}
        <div className="h-16 flex items-center justify-between px-6 border-b border-teal-700/50 shrink-0 bg-teal-900/30">
          <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-xl shadow-md w-full max-w-[180px]">
            <div className="h-8 w-10 overflow-hidden flex items-center justify-center shrink-0">
              <img src={logo} alt="Logo" className="w-16 max-w-none -mt-4 object-cover" />
            </div>
            <span className="font-bold text-[13px] text-teal-800 leading-tight tracking-tight">UNiFy Rx<br/>CRM</span>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-teal-200 hover:text-white bg-white/10 p-1.5 rounded-md ml-2">
            <X size={20} />
          </button>
        </div>

        {/* Pharmacy Selector Info */}
        <div className="px-4 py-4">
          <div className="bg-teal-900/50 border border-teal-600/30 rounded-xl p-3 flex items-center gap-3 shadow-sm">
            <div className="h-9 w-9 rounded-lg bg-white text-teal-800 flex items-center justify-center shrink-0 shadow-sm">
              <Building2 size={18} />
            </div>
            <div className="overflow-hidden">
              <p className="text-[10px] font-bold text-teal-200 uppercase tracking-wider mb-0.5">Active Pharmacy</p>
              <p className="text-sm font-semibold text-white truncate">A & G Pharmacy</p>
            </div>
          </div>
        </div>

        {/* Main Navigation */}
        <nav className="flex-1 px-4 overflow-y-auto space-y-2">
          <p className="text-[11px] font-bold text-teal-300/70 uppercase tracking-wider mb-3 px-2 mt-2">Menu</p>
          {navItems.map((item) => {
            const active = isActive(item.path);
            const Icon = item.icon;
            return (
              <Link 
                key={item.path} 
                to={item.path} 
                onClick={() => setSidebarOpen(false)} 
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 font-medium text-sm group ${
                  active 
                    ? 'bg-white text-teal-800 shadow-md transform scale-[1.02]' 
                    : 'text-teal-100 hover:bg-teal-700/50 hover:text-white'
                }`}
              >
                <div className={`p-1.5 rounded-md ${active ? 'bg-teal-50' : 'bg-transparent'}`}>
                  <Icon size={18} className={active ? 'text-teal-700' : 'text-teal-200 group-hover:text-white'} /> 
                </div>
                {item.name}
              </Link>
            );
          })}
        </nav>

        {/* Bottom Actions */}
        <div className="p-4 border-t border-teal-700/50 space-y-2 bg-teal-900/30">
          <Link 
            to="/recycle-bin" 
            onClick={() => setSidebarOpen(false)} 
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 font-medium text-sm group ${
              isActive('/recycle-bin') 
                ? 'bg-rose-500 text-white shadow-md' 
                : 'text-teal-100 hover:bg-rose-500/20 hover:text-rose-100'
            }`}
          >
            <div className={`p-1.5 rounded-md ${isActive('/recycle-bin') ? 'bg-rose-600' : 'bg-transparent'}`}>
              <Trash2 size={18} className={isActive('/recycle-bin') ? 'text-white' : 'text-teal-200 group-hover:text-rose-200'} /> 
            </div>
            Recycle Bin
          </Link>
          <button 
            onClick={onLogout} 
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 font-medium text-sm text-teal-100 hover:bg-teal-700/50 hover:text-white group"
          >
            <div className="p-1.5 rounded-md bg-transparent">
              <LogOut size={18} className="text-teal-200 group-hover:text-white" />
            </div>
            Logout
          </button>
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <div className="flex-1 flex flex-col overflow-hidden w-full relative bg-transparent">
        
        {/* HEADER */}
        <header className="bg-white/80 backdrop-blur-md border-b border-teal-100 h-16 flex items-center justify-between px-4 lg:px-8 z-20 shrink-0 sticky top-0 shadow-sm">
          <div className="flex items-center gap-3">
            {/* HAMBURGER MENU */}
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-xl transition shadow-sm border border-teal-100">
              <Menu size={20} />
            </button>
            
            {/* Logo for mobile/desktop top header */}
            <div className="flex items-center gap-1.5 bg-teal-50 px-2 py-1 rounded-xl border border-teal-100 shadow-sm lg:hidden">
              <div className="h-6 w-8 overflow-hidden flex items-center justify-center shrink-0">
                <img src={logo} alt="Logo" className="w-12 max-w-none -mt-3 object-cover" />
              </div>
              <span className="font-bold text-[11px] text-teal-800 leading-tight">UNiFy Rx<br/>CRM</span>
            </div>

            {/* Breadcrumb / Title area */}
            <div className="hidden sm:flex items-center">
               <div className="bg-teal-50 border border-teal-100 px-4 py-1.5 rounded-xl shadow-sm">
                 <h1 className="text-lg font-bold text-teal-800 capitalize">
                   {location.pathname === '/' ? 'Dashboard' : location.pathname.split('/')[1].replace('-', ' ')}
                 </h1>
               </div>
            </div>
          </div>

          {/* Right: Notifications, User */}
          <div className="flex items-center gap-4">
            <div className="relative" ref={notifRef}>
              <button onClick={() => setShowNotif(!showNotif)} className="relative p-2.5 bg-white text-teal-700 hover:bg-teal-50 rounded-xl transition-colors shadow-sm border border-teal-100">
                <Bell size={20} />
                {notifications.length > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 w-5 bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full ring-2 ring-white shadow-md">
                    {notifications.length > 9 ? '9+' : notifications.length}
                  </span>
                )}
              </button>
              
              {showNotif && (
                <div className="absolute right-0 mt-3 w-80 bg-white rounded-xl shadow-xl shadow-slate-200/50 border border-slate-200 overflow-hidden z-50 origin-top-right animate-in fade-in slide-in-from-top-2 duration-200">
                  <div className="p-3 border-b border-slate-100 bg-slate-50/80 backdrop-blur-sm flex justify-between items-center">
                    <h3 className="font-semibold text-slate-800 text-sm">Notifications</h3>
                    <div className="flex items-center gap-2">
                      {'Notification' in window && Notification.permission !== 'granted' && Notification.permission !== 'denied' && (
                        <button 
                          onClick={requestNotificationPermission}
                          className="text-[10px] font-bold bg-teal-100 text-teal-700 hover:bg-teal-200 px-2 py-1 rounded-md transition"
                        >
                          Enable Desktop Alerts
                        </button>
                      )}
                      <span className="text-xs font-medium text-slate-500 bg-slate-200/50 px-2 py-0.5 rounded-full">{notifications.length} New</span>
                    </div>
                  </div>
                  <div className="max-h-[320px] overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-8 text-center text-slate-400 text-sm flex flex-col items-center gap-2">
                         <Bell className="w-8 h-8 text-slate-200" />
                         No new alerts
                      </div>
                    ) : (
                      notifications.map((n, i) => (
                        <div key={i} onClick={() => handleNotificationClick(n.rx_id, n.patient_id, n.type)} className="p-3.5 border-b border-slate-50 hover:bg-slate-50 transition flex items-start gap-3 cursor-pointer">
                          <div className="mt-0.5 shrink-0 bg-white p-1.5 rounded-full shadow-sm border border-slate-100">
                            {n.type === 'Expiration' && <Clock size={14} className="text-orange-500" />}
                            {n.type === 'Pickup' && <Package size={14} className="text-rose-500" />}
                            {n.type === 'Refill' && <RefreshCw size={14} className="text-teal-500" />}
                            {n.type === 'Birthday' && <Gift size={14} className="text-purple-500" />}
                            {n.type === 'Activity' && <Activity size={14} className="text-blue-500" />}
                          </div>
                          <div className="min-w-0 flex-1">
                            {n.type === 'Activity' ? (
                              <>
                                <p className="text-sm font-semibold text-slate-800 truncate">{n.action_type || 'Activity Logged'}</p>
                                <p className="text-xs text-slate-500 truncate">{n.details || `${n.full_name || 'Patient'} - ${n.drug_name || 'Rx'}`}</p>
                              </>
                            ) : (
                              <>
                                <p className="text-sm font-semibold text-slate-800 truncate">{n.drug_name}</p>
                                <p className="text-xs text-slate-500 truncate">{n.full_name}</p>
                              </>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="h-6 w-px bg-teal-200 hidden sm:block"></div>

            <div className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition bg-white px-3 py-1.5 rounded-xl border border-teal-100 shadow-sm">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-semibold text-teal-900 leading-tight">{displayName}</p>
                <p className="text-[10px] text-teal-600 font-bold uppercase tracking-wider">{user.user_code || 'Admin'}</p>
              </div>
              <div className="h-9 w-9 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-sm ring-2 ring-white">
                {initial}
              </div>
            </div>
          </div>
        </header>

        {/* PAGE CONTENT */}
        <main className="flex-1 overflow-auto p-4 lg:p-6 w-full">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;